import { Button } from "@/components/ui/UIElements/Button";
import React, { useEffect, useState } from "react";
import { useToastContext } from "@finitless/shared/contexts";
import {
  Icon,
  Modal,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "../../";
import { Pagination } from "../../Data";
import { useTaxes } from "../../../../hooks";
import TaxFormModal from "./TaxFormModal";

const TaxesStep = ({
  businessId,
  isUpdatingProducts = false,
  selectedTaxId,
  onAssigned,
}) => {
  const toast = useToastContext();
  const [showForm, setShowForm] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [currentTaxId, setCurrentTaxId] = useState(selectedTaxId ?? null);

  const {
    taxes,
    pagination,
    page,
    setPagination,
    isLoading,
    isFetching,
    createTax,
    isCreating,
    updateTax,
    isUpdating,
    deleteTax,
    isDeleting,
    assignTaxToBusiness,
    isAssigning,
  } = useTaxes({ pageSize: 10 });

  const isBusy =
    isLoading ||
    isFetching ||
    isCreating ||
    isUpdating ||
    isDeleting ||
    isAssigning;

  useEffect(() => {
    setCurrentTaxId(selectedTaxId ?? null);
  }, [selectedTaxId]);

  const handleCreate = async (payload) => {
    try {
      const res = await createTax(payload);
      setShowForm(false);
      if (res?.result?.id) {
        toast.success("Tax created");
      }
    } catch (e) {
      console.error("Create tax failed", e);
    }
  };

  const handleEdit = async (payload) => {
    try {
      await updateTax({ taxId: editingTax.id, payload });
      setEditingTax(null);
    } catch (e) {
      console.error("Update tax failed", e);
    }
  };

  const handleAssign = async (tax) => {
    try {
      const unassign = currentTaxId === tax.id;
      if (!isUpdatingProducts) {
        await assignTaxToBusiness({
          businessId,
          taxId: unassign ? null : tax.id,
        });
      }
      setCurrentTaxId(unassign ? null : tax.id);
      if (onAssigned) onAssigned(unassign ? null : tax);
      if (unassign) {
        toast.success("Tax unassigned");
      } else {
        toast.success("Tax assigned");
      }
    } catch (e) {
      console.error("Assign tax failed", e);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTax(confirmDelete.id);
      setConfirmDelete(null);
    } catch (e) {
      console.error("Delete tax failed", e);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-foreground text-base font-medium sm:text-lg">
            Taxes
          </h3>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Select a tax for this business or manage your taxes.
          </p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1 px-3 py-2"
        >
          <Icon name="Plus" className="h-4 w-4" /> Add Tax
        </Button>
      </div>

      <div className="border-border bg-card/60 overflow-x-auto rounded-lg border">
        <div className="border-border bg-secondary text-foreground grid min-w-[42rem] grid-cols-[minmax(9rem,1fr)_4rem_7rem_6rem_13rem] border-b px-4 py-3 text-[length:var(--font-size-product-ui)]">
          <div>Name</div>
          <div>Rate</div>
          <div>Type</div>
          <div>Custom rates</div>
          <div className="text-end">Actions</div>
        </div>

        {isLoading ? (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="border-border grid min-w-[42rem] animate-pulse grid-cols-[minmax(9rem,1fr)_4rem_7rem_6rem_13rem] items-center border-t px-4 py-3"
              >
                <div>
                  <div className="bg-secondary mb-2 h-4 w-3/4 rounded"></div>
                  <div className="bg-secondary h-3 w-1/2 rounded"></div>
                </div>
                <div>
                  <div className="bg-secondary h-6 w-12 rounded"></div>
                </div>
                <div>
                  <div className="bg-secondary h-6 w-[90%] rounded"></div>
                </div>
                <div>
                  <div className="bg-secondary h-6 w-8 rounded"></div>
                </div>
                <div className="flex items-center justify-end gap-2">
                  <div className="bg-secondary h-7 w-16 rounded"></div>
                  <div className="bg-secondary h-7 w-12 rounded"></div>
                  <div className="bg-secondary h-7 w-14 rounded"></div>
                </div>
              </div>
            ))}
          </>
        ) : taxes.length === 0 ? (
          <div className="text-foreground p-8 text-center">No taxes found</div>
        ) : (
          taxes.map((t) => {
            const isSelected = currentTaxId === t.id;
            const customCount = ["delivery", "pickup"].filter(
              (k) => !!t.order_type_rates?.[k],
            ).length;
            return (
              <div
                key={t.id}
                className={`text-foreground grid min-w-[42rem] grid-cols-[minmax(9rem,1fr)_4rem_7rem_6rem_13rem] items-center border-t px-4 py-3 transition-colors ${
                  isSelected
                    ? "border-success/30 bg-success/10 shadow-[var(--od-shadow-1)]"
                    : "border-border hover:bg-secondary/40"
                }`}
              >
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-muted-foreground truncate text-xs">
                    {t.description || "—"}
                  </div>
                </div>
                <div>
                  <span className="border-border bg-secondary rounded border px-2 py-0.5 text-xs">
                    {t.rate}%
                  </span>
                </div>
                <div>
                  <span className="border-primary/40 bg-primary/20 block w-[90%] rounded border px-2 py-0.5 text-center text-xs text-[var(--od-action-content)]">
                    {t.type === 1 ? "Included" : "Not included"}
                  </span>
                </div>
                <div>
                  {customCount > 0 ? (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="ghost"
                          className="border-border bg-secondary hover:bg-muted rounded border px-2 py-0.5"
                        >
                          {customCount}
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent side="top" className="w-64">
                        <div className="text-foreground mb-2 text-sm">
                          Custom rates
                        </div>
                        <div className="text-foreground space-y-1 text-xs">
                          {t.order_type_rates?.delivery && (
                            <div className="flex items-center justify-between">
                              <span>Delivery</span>
                              <span className="border-border bg-secondary rounded border px-2 py-0.5">
                                {t.order_type_rates.delivery.rate}% (
                                {t.order_type_rates.delivery.type === 1
                                  ? "Included"
                                  : "Not included"}
                                )
                              </span>
                            </div>
                          )}
                          {t.order_type_rates?.pickup && (
                            <div className="flex items-center justify-between">
                              <span>Pickup</span>
                              <span className="border-border bg-secondary rounded border px-2 py-0.5">
                                {t.order_type_rates.pickup.rate}% (
                                {t.order_type_rates.pickup.type === 1
                                  ? "Included"
                                  : "Not included"}
                                )
                              </span>
                            </div>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  ) : (
                    <span className="border-border bg-secondary rounded border px-2 py-0.5 text-xs">
                      0
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <Button
                    variant={isSelected ? "destructive" : "ghost"}
                    onClick={() => handleAssign(t)}
                    disabled={isBusy}
                    className={`rounded border px-2 py-1 transition-colors ${isSelected ? "border-destructive" : "border-border bg-card hover:bg-secondary"}`}
                    title={
                      isSelected
                        ? "Unassign from business"
                        : "Assign to business"
                    }
                  >
                    {isSelected ? "Unassign" : "Assign"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setEditingTax(t)}
                    disabled={isBusy}
                    className="border-border bg-card hover:bg-secondary rounded border px-2 py-1"
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setConfirmDelete(t)}
                    disabled={isBusy}
                    className="border-destructive/60 text-destructive hover:bg-destructive/30 rounded border px-2 py-1"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination (match ProductsIndex.jsx) */}
      {pagination?.total_pages > 1 && (
        <div className="mt-4">
          <div className="border-border bg-card rounded-lg border p-4">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="text-muted-foreground text-sm">
                {(() => {
                  const pageSizeLocal = pagination?.page_size || 10;
                  const total = pagination?.total || 0;
                  const currentPage = page || 1;
                  const start = total
                    ? (currentPage - 1) * pageSizeLocal + 1
                    : 0;
                  const end = Math.min(currentPage * pageSizeLocal, total);
                  return (
                    <>
                      Showing{" "}
                      <span className="text-foreground font-medium">
                        {start}
                      </span>{" "}
                      to{" "}
                      <span className="text-foreground font-medium">{end}</span>{" "}
                      of{" "}
                      <span className="text-foreground font-medium">
                        {total}
                      </span>{" "}
                      taxes
                    </>
                  );
                })()}
              </div>

              <Pagination
                currentPage={page}
                totalPages={pagination.total_pages}
                onPageChange={setPagination}
                maxVisiblePages={5}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal (single instance) */}
      {(showForm || editingTax) && (
        <TaxFormModal
          isOpen={true}
          onClose={() => {
            if (editingTax) setEditingTax(null);
            if (showForm) setShowForm(false);
          }}
          onSubmit={editingTax ? handleEdit : handleCreate}
          initialValues={editingTax || undefined}
          isSubmitting={editingTax ? isUpdating : isCreating}
        />
      )}

      {/* Delete Confirm (match AccountPage modal style) */}
      {confirmDelete && (
        <Modal
          className="ordering-catalog-surface"
          isOpen={true}
          onClose={() => setConfirmDelete(null)}
          title="Delete Tax"
          subtitle="Are you sure you want to delete this tax?"
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            <div className="border-destructive bg-destructive/10 dark:border-destructive dark:bg-destructive/20 rounded-lg border p-4">
              <div className="flex items-center space-x-3">
                <Icon
                  name="AlertTriangle"
                  className="text-destructive h-5 w-5"
                />
                <div>
                  <h4 className="text-destructive dark:text-destructive text-sm font-medium">
                    {confirmDelete.name}
                  </h4>
                  <p className="text-destructive dark:text-destructive mt-1 text-xs">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-muted-foreground text-sm">
              Deleting this tax will permanently remove it from your account.
            </p>

            <div className="flex space-x-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(null)}
                disabled={isDeleting}
                className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex flex-1 items-center justify-center space-x-2 px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeleting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                ) : null}
                <span>{isDeleting ? "Deleting..." : "Delete Tax"}</span>
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TaxesStep;
